import React, { Component, ReactNode } from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { CardElement } from "@stripe/react-stripe-js";
import {
  Stripe,
  StripeElements,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
} from "@stripe/stripe-js";
import get from "lodash.get";

import { withStripe } from "./withStripe";
import {
  makePaymentRequest,
  setPaymentStarted,
  makePaymentErrorOccured,
  onValueChange,
  makePaymentIntentPublic,
} from "../reducer";

import { Form, Divider } from "semantic-ui-react";

import { AnalyticsHelper } from "../../../helpers/analyticsHelper";
import { getErrorMessage } from "../../../helpers/donationErrorHelper";

import Card from "./card";
import CoverFee from "./coverFee";
import DonateButton from "./donationButton";
import PaymentRequestForm from "./paymentRequestForm";
import DonationErrorMessage from "./donationErrorMessage";
import { InjectReCaptcha } from "../../../components/common/reCaptcha";

import { ActionNames } from "../../../constants/webTracking";
import { displayNameKeys } from "../../../constants/displayNameOptions";
import { erorrMessages } from "../constants/errorMessages";
import { loadingStatus } from "../../../constants/loading";
import { MAX_DONATION_VALUE } from "../../../constants";
import {
  DONATE_KEY,
  WALLETS,
  DONATION_VIA_WALLET_RECAPTCHA_KEY,
  StripePayment,
} from "./stripePayment";
import { paths } from "../constants";

import DonationService from "../../../services/donationService";
import IdempotencyKeyService from "../../../services/idempotencyKeyService";

import "./index.css";
import { isValidationFormValid } from "../../../helpers/validationHelper";
import {
  isLegacyDonationBehaviour,
  isMotoDonation,
} from "../../../helpers/donationBehaviourHelper";
import { executeWithRecaptcha } from "../../../components/common/reCaptcha";

interface IMakePaymentFormContainerProps extends RouteComponentProps {
  stripe: Stripe | null;
  elements: StripeElements | null;
  donation: any;
  page: any;
  onValueChange: (path: string, value: any) => void;
  makePayment: (
    donation: any,
    idempotencyKey: string,
    verificationToken: string | null
  ) => void;
  setPaymentStarted: () => void;
  makePaymentErrorOccured: (error: any) => void;
  makePaymentIntentPublic: (
    guid: string,
    verificationToken: string | null,
    walletInfoMessage: ReactNode
  ) => void;
}

interface IMakePaymentFormContainerState {
  canMakePayment: boolean;
  paymentRequest: PaymentRequest | null;
  currentDonation?: any;
}

class MakePaymentFormContainer extends Component<
  IMakePaymentFormContainerProps,
  IMakePaymentFormContainerState
> {
  state: IMakePaymentFormContainerState = {
    canMakePayment: false,
    paymentRequest: null,
  };

  componentDidMount() {
    // `stripe` arrives asynchronously from the <Elements> provider (loadStripe
    // promise). Set up the wallet payment request as soon as it is available.
    if (this.props.stripe) {
      this.initWalletPaymentRequest();
    }
  }

  initWalletPaymentRequest = () => {
    const { stripe } = this.props;
    if (!stripe) return;

    const disablePayments =
      this.props?.page?.campaign?.template?.value?.customSetup
        ?.standaloneDonationForm?.disableWalletPayments;
    if (disablePayments) return;

    // MOTO campaigns never offer wallets: an Apple/Google Pay tap is a customer-authenticated
    // payment, which must not be flagged as mail-order/telephone-order (SCA-exempt).
    if (isMotoDonation(this.props.page)) return;

    const paymentRequest = stripe.paymentRequest({
      country: this.props?.page?.campaign?.organization?.country,
      currency:
        this.props?.page?.campaign?.organization?.currency?.toLowerCase(),
      total: {
        label: this.props.donation.amountText,
        amount: parseInt(
          (this.props.donation.paymentRequestAmount * 100).toString()
        ),
      },
    });

    paymentRequest.canMakePayment().then((result) => {
      if (result && (result.applePay || result.googlePay)) {
        this.setState({ canMakePayment: !!result });
      }
    });

    const donation = StripePayment.getDonationForPaymentIntent(
      this.props.donation,
      this.props.page
    );
    // @ts-ignore - removeAllListeners isn't in the @stripe/stripe-js PaymentRequest type
    paymentRequest.removeAllListeners("paymentmethod");
    paymentRequest.on("paymentmethod", (ev) =>
      this.paymentMethodCallback(ev, donation)
    );

    this.setState({
      paymentRequest,
      currentDonation: donation,
    });
  }

  paymentMethodCallback = (
    ev: PaymentRequestPaymentMethodEvent,
    donation: any
  ) => {
    const { stripe } = this.props;
    if (!stripe) return;

    DonationService.makePaymentIntent(
      donation,
      IdempotencyKeyService.getIdempotencyKey()
    ).then((result) => {
      stripe
        .confirmCardPayment(
          result.data.data.paymentIntentIdentifier,
          { payment_method: ev.paymentMethod.id },
          // @ts-ignore
          { handleActions: false }
        )
        .then((confirmResult) => {
          if (confirmResult.error) {
            // Report to the browser that the payment failed, prompting it to
            // re-show the payment interface, or show an error message and close
            // the payment interface.
            ev.complete("fail");

            this.props.makePaymentErrorOccured(confirmResult.error);
          } else {
            // Report to the browser that the confirmation was successful, prompting
            // it to close the browser payment method collection interface.
            ev.complete("success");
            const walletInfoMessage = (
              <React.Fragment>
                Your donation was successfully processed using{" "}
                <strong>
                  {WALLETS.get(ev.walletName)} (
                  {ev.paymentMethod.card?.brand?.toUpperCase()} ****{" "}
                  {ev.paymentMethod.card?.last4})
                </strong>
              </React.Fragment>
            );

            // Check if the PaymentIntent requires any actions and if so let Stripe.js
            // handle the flow. If using an API version older than "2019-02-11"
            // instead check for: `paymentIntent.status === "requires_source_action"`.
            if (confirmResult.paymentIntent?.status === "requires_action") {
              // Let Stripe.js handle the rest of the payment flow.
              stripe
                .confirmCardPayment(result.data.data.paymentIntentIdentifier)
                .then((secondConfirm) => {
                  if (secondConfirm.error) {
                    // The payment failed -- ask your customer for a new payment method.
                    this.props.makePaymentErrorOccured(secondConfirm.error);
                  } else {
                    // The payment has succeeded.
                    executeWithRecaptcha(
                      (token: any) =>
                        this.props.makePaymentIntentPublic(
                          result.data.data.identifier,
                          token,
                          walletInfoMessage
                        ),
                      DONATION_VIA_WALLET_RECAPTCHA_KEY
                    );
                  }
                });
            } else {
              // The payment has succeeded.
              executeWithRecaptcha(
                (token: any) =>
                  this.props.makePaymentIntentPublic(
                    result.data.data.identifier,
                    token,
                    walletInfoMessage
                  ),
                DONATION_VIA_WALLET_RECAPTCHA_KEY
              );
            }
          }
        })
        .catch((error) => {
          this.props.makePaymentErrorOccured(error);
        });
    });
  };

  componentDidUpdate(prevProps: IMakePaymentFormContainerProps) {
    // Stripe became available after mount — set up the wallet payment request.
    if (!prevProps.stripe && this.props.stripe && !this.state?.paymentRequest) {
      this.initWalletPaymentRequest();
    }

    if (this.props.donation.paymentInProgress === loadingStatus.SUCCESS) {
      const amount = get(this.props, "donation.createdPaymentRecord.amount");
      const currency = get(
        this.props,
        "donation.createdPaymentRecord.financials.currency"
      );
      const campaignName = get(
        this.props,
        "donation.createdPaymentRecord.campaign.name"
      );
      const paymentId = get(this.props, "donation.createdPaymentRecord.id");

      AnalyticsHelper.createEvent(
        campaignName,
        ActionNames.donation,
        window.location.pathname,
        amount,
        currency
      );
      AnalyticsHelper.executeEcommercePlugin(
        paymentId,
        campaignName,
        amount,
        currency,
        ActionNames.donation,
        null
      );

      if (this.props.match.url.endsWith("/")) {
        this.props.history.push(`${this.props.match.url}thankyou`);
      } else {
        this.props.history.push(`${this.props.match.url}/thankyou`);
      }
    } else {
      const donation = StripePayment.getDonationForPaymentIntent(
        this.props.donation,
        this.props.page
      );
      const sameDonation = prevProps.donation === this.props.donation;
      const { paymentRequest } = this.state;
      // only update the payment request if the donation has changed
      if (paymentRequest && !sameDonation) {
        const amount = this.props.donation?.paymentRequestAmount;
        paymentRequest.update({
          total: {
            label: this.props.donation.amountText,
            amount: parseInt((amount * 100).toString()),
          },
        });

        this.setState({
          currentDonation: donation,
        });

        // @ts-ignore - removeAllListeners isn't in the @stripe/stripe-js PaymentRequest type
        paymentRequest.removeAllListeners("paymentmethod");
        paymentRequest.on("paymentmethod", (ev) =>
          this.paymentMethodCallback(ev, donation)
        );
      }
    }
  }

  handleSubmit = (ev: any) => {
    executeWithRecaptcha(
      (token: any) => this.proceedSubmit(ev, token),
      DONATE_KEY
    );
  };

  proceedSubmit = (ev: any, verificationToken: string | null = null) => {
    this.props.setPaymentStarted();

    // We don't want to let default form submission happen here, which would refresh the page.
    ev.preventDefault();

    // Kill-switch (GR-3597): legacy token charge vs new PaymentIntent.
    if (isLegacyDonationBehaviour(this.props.page)) {
      this.proceedSubmitLegacy(verificationToken);
    } else {
      this.proceedSubmitIntent(verificationToken);
    }
  };

  // New default: PaymentIntent flow (mirrors the wallet flow in paymentMethodCallback) —
  // create the intent server-side, then confirm the card against its client secret.
  proceedSubmitIntent = (verificationToken: string | null) => {
    const { stripe, elements } = this.props;
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      this.props.makePaymentErrorOccured(new Error("Card details are missing."));
      return;
    }

    const donation = StripePayment.getDonationForPaymentIntent(
      this.props.donation,
      this.props.page
    );

    if (isMotoDonation(this.props.page)) {
      this.proceedSubmitMoto(cardElement, donation, verificationToken);
      return;
    }

    DonationService.makePaymentIntent(
      donation,
      IdempotencyKeyService.getIdempotencyKey()
    )
      .then((result) => {
        stripe
          .confirmCardPayment(result.data.data.paymentIntentIdentifier, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: `${this.props.donation.customer.firstName} ${this.props.donation.customer.lastName}`,
                email: this.props.donation.customer.email,
              },
            },
          })
          .then((confirmResult) => {
            if (confirmResult.error) {
              this.props.makePaymentErrorOccured(confirmResult.error);
            } else {
              this.props.makePaymentIntentPublic(
                result.data.data.identifier,
                verificationToken,
                null
              );
            }
          });
      })
      .catch((error) => {
        this.props.makePaymentErrorOccured(error);
      });
  };

  // MOTO (agent-assisted) checkout: Stripe accepts the moto option only alongside a
  // confirmation, so the card becomes a PaymentMethod here and the API confirms the intent
  // server-side on creation. No confirmCardPayment step follows: MOTO is SCA-exempt (no 3DS
  // leg), and a decline surfaces as the intent-creation error.
  proceedSubmitMoto = (
    cardElement: any,
    donation: any,
    verificationToken: string | null
  ) => {
    const { stripe } = this.props;
    if (!stripe) return;

    stripe
      .createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: `${this.props.donation.customer.firstName} ${this.props.donation.customer.lastName}`,
          email: this.props.donation.customer.email,
        },
      })
      .then((pmResult) => {
        if (pmResult.error || !pmResult.paymentMethod) {
          this.props.makePaymentErrorOccured(
            pmResult.error || new Error("Card details could not be processed.")
          );
          return;
        }

        DonationService.makePaymentIntent(
          { ...donation, paymentMethodId: pmResult.paymentMethod.id },
          IdempotencyKeyService.getIdempotencyKey()
        )
          .then((result) => {
            this.props.makePaymentIntentPublic(
              result.data.data.identifier,
              verificationToken,
              null
            );
          })
          .catch((error) => {
            this.props.makePaymentErrorOccured(error);
          });
      })
      .catch((error) => {
        this.props.makePaymentErrorOccured(error);
      });
  };

  // Legacy kill-switch: synchronous token charge (createToken -> /api/v2/donation).
  proceedSubmitLegacy = (verificationToken: string | null) => {
    const { stripe, elements } = this.props;
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      this.props.makePaymentErrorOccured(new Error("Card details are missing."));
      return;
    }

    stripe
      .createToken(cardElement, {
        name: `${this.props.donation.customer.firstName} ${this.props.donation.customer.lastName}`,
      })
      .then(this.submitPayment.bind(this, verificationToken))
      .catch((error) => {
        this.props.makePaymentErrorOccured(error);
      });
  };

  submitPayment = (verificationToken: string | null, { complete, token }: any) => {
    const donation = StripePayment.getDonation(
      this.props.donation,
      this.props.page,
      token
    );
    this.props.makePayment(
      donation,
      this.props.donation.idempotencyKey,
      verificationToken
    );
    if (complete) {
      complete("success");
    }
  };

  getDisplayName = () => {
    const displayNameOption = get(
      this.props.donation,
      paths.SELECTED_DISPLAY_NAME_OPTION
    );
    return displayNameOption === displayNameKeys.asCustomName
      ? get(this.props, "donation.displayName")
      : null;
  };

  isTooMuchValue = () => {
    return (
      get(this.props, "donation.paymentRequestAmount") > MAX_DONATION_VALUE
    );
  };

  render() {
    const donationAmount = get(this.props, "donation.amount");
    const serverErrorOccured = get(this.props, "donation.serverErrorOccured");
    const serverErrorKey = get(this.props, "donation.serverErrorKey");
    const isTooMuchValue = this.isTooMuchValue();
    const isValid = isValidationFormValid(this.props.donation.tabsValidation);
    const isLoading =
      this.props.donation.paymentInProgress === loadingStatus.IS_LOADING ||
      this.props.donation.fetchingFeeInProgress;

    return (
      <Form onSubmit={this.handleSubmit}>
        {this.state?.canMakePayment && (
          <>
            <Form.Field>
              <PaymentRequestForm
                isDisabled={!isValid || isLoading}
                paymentRequest={this.state.paymentRequest}
                canMakePayment={this.state.canMakePayment}
              />
            </Form.Field>

            <Divider className="switch-payment-method-divider" horizontal>
              or pay with card
            </Divider>
          </>
        )}

        <Form.Field>
          <Card />
        </Form.Field>

        <Form.Field>
          {/* @ts-ignore */}
          <CoverFee amount={donationAmount} />
        </Form.Field>

        <DonateButton disabled={isTooMuchValue} />

        {isTooMuchValue && (
          <DonationErrorMessage
            dismiss={false}
            errorMessage={erorrMessages.MaxValueError}
          />
        )}
        {serverErrorOccured && (
          <DonationErrorMessage
            dismiss={true}
            errorMessage={getErrorMessage(serverErrorKey)}
          />
        )}
      </Form>
    );
  }
}

const mapState = (state: any) => {
  return {
    donation: state.donation,
    page: state.page,
  };
};

const mapDispatch = (dispatch: any) => {
  return {
    onValueChange(path: string, value: any) {
      dispatch(onValueChange(path, value));
    },
    makePayment: (
      donation: any,
      idempotencyKey: string,
      verificationToken: string
    ) => {
      dispatch(makePaymentRequest(donation, idempotencyKey, verificationToken));
    },
    setPaymentStarted: () => {
      dispatch(setPaymentStarted());
    },
    makePaymentIntentPublic: (
      guid: string,
      verificationToken: string,
      walletInfoMessage: string
    ) => {
      dispatch(
        makePaymentIntentPublic(guid, verificationToken, walletInfoMessage)
      );
    },
    makePaymentErrorOccured: (error: any) => {
      dispatch(makePaymentErrorOccured(error));
    },
  };
};

// @ts-ignore
const MakePaymentForm = withRouter(
  connect(
    mapState,
    mapDispatch
  )(InjectReCaptcha(withStripe(MakePaymentFormContainer as any)))
);
export default MakePaymentForm;
